// @ts-check
// ── The Renewals card: programs ending in the next 60 days ───────────────────
//
// Part 5 of docs/superpowers/plans/2026-10-14-dashboard-build.md, from the approved prototype
// (mockups/ultimate-dashboard-v3-2026-10-13.html: the Renewals card, renewChart) on the server's `renewals`
// (_shared/dashboard/renewals.js). The steps are the Renewals page's, so Open Renewals lands on the same counts.

import { money, bigMoney, plural, shortDay } from './words.js';
import { info, status, ICON } from './tiles.js';
import { tipRows, table } from './charts.js';

/**
 * The card. Empty when nothing ends in the next 60 days.
 * @param {any} R the server's `renewals`
 */
export function renewalsCardHtml(R) {
  const head = `<div class="dv-card-head"><div class="dv-head-l"><h2 class="dv-title" id="dv-rn-h">Renewals</h2>${info('renewals')}</div>
      <button type="button" class="dv-link" data-open-renewals>Open Renewals${ICON.go}</button></div>`;
  if (!R.count) return `<section class="dv-card dv-s4 dv-rn" aria-labelledby="dv-rn-h">${head}<div class="dv-empty dv-empty--short">${status('none', 'No programs end in the next 60 days')}</div></section>`;
  const rows = /** @type {any[]} */ (R.steps).map((s) => `<div class="dv-rn-row${s.count ? '' : ' is-none'}"><span class="dv-rn-l">${s.label}</span><b>${s.count || ''}</b><span class="dv-rn-m">${s.count ? `${money(s.monthly)} a month` : 'None'}</span></div>`).join('');
  return `<section class="dv-card dv-s4 dv-rn" aria-labelledby="dv-rn-h">${head}
      <div class="dv-value"><span class="dv-num">${bigMoney(R.monthly)}</span><span class="dv-of">a month up for renewal</span></div>
      <div class="dv-sub"><b>${plural(R.count, 'program ends', 'programs end')}</b> in the next 60 days</div>
      <div class="dv-rn-ph">Programs ending each week</div>
      <div class="dv-plotbox dv-mini dv-fill dv-rn-plot" data-chart="renew"></div>
      <div class="dv-rn-rows">${rows}</div>
    </section>`;
}

/**
 * Programs ending each week for the next 60 days: 9 columns of 7 days from today (the last is 5). It fills the
 * card's spare height, so the row stays even.
 * @param {Array<{ from: string, to: string, count: number, monthly: number }>} weeks @param {number} W @param {number} HB the box's free height
 */
export function renewChart(weeks, W, HB) {
  const n = weeks.length, h = Math.round(Math.max(86, Math.min(320, HB - 4))), base = h - 24, top = 16;
  const slot = W / n, bw = Math.min(22, slot * 0.55), max = Math.max(1, ...weeks.map((w) => w.count));
  const peak = weeks.findIndex((w) => w.count === max);
  const y = (/** @type {number} */ c) => base - ((base - top) * c) / max;
  const label = `Programs ending each week for the next 60 days: ${weeks.map((w) => `week of ${shortDay(w.from)}, ${w.count ? plural(w.count, 'program', 'programs') : 'none'}`).join('; ')}.`;
  let s = `<svg viewBox="0 0 ${W} ${h}" class="dv-plot" tabindex="0" role="img" aria-label="${label}"><line class="base" x1="0" x2="${W}" y1="${base}" y2="${base}"/>`;
  weeks.forEach((w, i) => {
    const bx = slot * i + (slot - bw) / 2, t = y(w.count);
    if (i % 2 === 0) s += `<text x="${bx + bw / 2}" y="${h - 4}" text-anchor="middle">${shortDay(w.from)}</text>`;
    s += `<g class="col" data-i="${i}" ${tipRows(w.from === w.to ? shortDay(w.from) : `${shortDay(w.from)} to ${shortDay(w.to)}`, [['Programs ending', String(w.count), 'bar'], ['Money a month', money(w.monthly), '']], null)}>`;
    if (w.count) s += `<path class="bar" d="M${bx},${base} V${t + 3} Q${bx},${t} ${bx + 3},${t} H${bx + bw - 3} Q${bx + bw},${t} ${bx + bw},${t + 3} V${base} Z"/>`;
    s += `<rect class="hit" x="${slot * i}" y="0" width="${slot}" height="${base}"/></g>`;
    if (w.count && (i === peak || i === 0)) s += `<text class="val" x="${bx + bw / 2}" y="${t - 6}" text-anchor="middle">${w.count}</text>`;
  });
  return s + '</svg>' + table('Programs ending each week', weeks.map((w) => [`${shortDay(w.from)} to ${shortDay(w.to)}`, `${w.count}, ${money(w.monthly)} a month`]));
}
